/**
 * Display helpers both sides of the wire agree on.
 *
 * A byte count is rendered in exactly one place so the API's own honest notes
 * ("3.9 GB free of 40 GB") and the browser's cards never disagree by a
 * rounding rule.
 */

/**
 * Binary units, because that is what `statfs`, `os.totalmem()` and SQLite all
 * count in. One decimal below ten, none above: "9.4 GB" is worth a digit,
 * "512 GB" is not.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/**
 * A distance along the ground, as the map's route labels and the structure
 * card's "feed: 1.2 km" both print it: whole metres below a kilometre, one
 * decimal of kilometres above. A fibre run is measured off a map, so a
 * decimal of metres would be precision the number does not have.
 */
export function formatDistance(metres: number | null | undefined): string {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) return '—';
  if (metres < 1000) return `${Math.round(metres).toLocaleString()} m`;
  return `${(metres / 1000).toFixed(1)} km`;
}

/**
 * Money.
 *
 * Everything financial in this platform is stored in MINOR units — integer
 * cents, so an amount can be added, split and compared without a float ever
 * touching it. This is the one place that turns those integers back into
 * something a person reads, and it lives here because the browser and the PDF
 * generator render the same invoice: two implementations of this rule would
 * mean a page and its printed copy disagreeing about a total, which is the
 * one bug an invoice cannot survive.
 */

/**
 * How many minor units make one major unit, where it is not 100.
 *
 * ISO 4217 is not uniform: the yen has no subdivision at all and the dinars
 * have three digits. Dividing everything by 100 would print ¥1,200 as ¥12.00.
 * Only the exceptions are listed; 100 is the default and covers KES and USD,
 * the two currencies this platform prices in today.
 */
const CURRENCY_EXPONENT: Record<string, number> = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0,
  RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
};

/** Digits after the decimal point for a currency's minor units. */
export function currencyExponent(currency: string): number {
  return CURRENCY_EXPONENT[currency.toUpperCase()] ?? 2;
}

/**
 * The symbol a payer recognises, where one exists.
 *
 * Deliberately limited to symbols that survive WinAnsiEncoding, which is what
 * a PDF's built-in fonts draw with: a symbol that comes out as a box on the
 * printed invoice is worse than the three-letter code. KES is "KSh", which is
 * how it is written on every Kenyan receipt. Absent means "use the code".
 */
const CURRENCY_SYMBOL: Record<string, string> = {
  KES: 'KSh', USD: '$', EUR: '€', GBP: '£', JPY: '¥',
  ZAR: 'R', TZS: 'TSh', UGX: 'USh',
};

export function currencySymbol(currency: string): string | null {
  return CURRENCY_SYMBOL[currency.toUpperCase()] ?? null;
}

/**
 * Groups the integer part in threes by hand rather than through `Intl`.
 *
 * `toLocaleString` was doing this before, and it makes the output depend on
 * which locale the caller happened to name and on the ICU data the runtime was
 * built with — a Node build without full ICU and a browser do not have to
 * agree, and some locales group in a way that would make the API's copy of a
 * total differ from the browser's character for character. An invoice is one
 * of the few places where "the same string everywhere" beats "the reader's own
 * convention".
 */
function group(digits: string): string {
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return out;
}

export interface MoneyOptions {
  /** Prefix with the currency's symbol instead of its ISO code, where one exists. */
  symbol?: boolean;
}

/**
 * `KES 12,340.50` — or `KSh 12,340.50` with `{ symbol: true }`.
 *
 * The code leads by default. On a platform that prices the same service in two
 * currencies, "$1,000" and "1,000" are very different bills, and the code is
 * the only rendering that cannot be misread.
 *
 * The split is integer arithmetic on purpose: `minor / 100` then `toFixed(2)`
 * rounds a value that was already exact, and at nine-figure amounts in minor
 * units that rounding is not reliably the identity.
 */
export function formatMoney(
  minorUnits: number | null | undefined,
  currency: string,
  options: MoneyOptions = {},
): string {
  const prefix = (options.symbol ? currencySymbol(currency) : null) ?? currency.toUpperCase();
  if (minorUnits === null || minorUnits === undefined || !Number.isFinite(minorUnits)) {
    return `${prefix} —`;
  }
  const exponent = currencyExponent(currency);
  const scale = 10 ** exponent;
  const rounded = Math.round(minorUnits);
  const sign = rounded < 0 ? '-' : '';
  const absolute = Math.abs(rounded);
  const whole = Math.trunc(absolute / scale);
  const fraction = absolute - whole * scale;
  const decimals = exponent > 0 ? `.${String(fraction).padStart(exponent, '0')}` : '';
  return `${sign}${prefix} ${group(String(whole))}${decimals}`;
}
